import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Dashboard from "./components/dashboard/Dashboard";
import LeadList from "./components/leads/LeadList";
import AddLead from "./components/leads/AddLead";
import ViewLead from "./components/leads/ViewLead";
import EditLead from "./components/leads/EditLead";
import AdminDashboard from "./components/admin/AdminDashboard";
import BookDemo from "./components/demo/BookDemo";
import NotificationsPage from "./components/notifications/NotificationsPage";
import ProfilePage from "./components/profile/ProfilePage";
import WelcomePending from "./components/auth/WelcomePending";
import DeveloperPortalPage from "./pages/DeveloperPortalPage";
import ManagerAnalyticsPage from "./components/analytics/ManagerAnalyticsPage";
import ScheduledExportsPanel from "./components/leads/ScheduledExportsPanel";
import AppShell from "./components/common/AppShell";
import AuthExpiredModal from "./components/auth/AuthExpiredModal";
import { LeadProvider } from "./contexts/LeadContext";
import ReportTemplateBuilder from "./components/reports/templates/ReportTemplateBuilder";

// Import order - CRITICAL
import "./styles/theme.css";
import "./styles/App.css";
import "./styles/components.css";
import "./styles/documents.css";
import "./styles/LeadPanel.css";
import "./styles/BulkImport.css";
import "./styles/Dashboard.css";
import "./styles/NextActivities.css";
import "./styles/SourceScope.css";
import "./styles/Integrations.css";
import "./styles/DeveloperPortal.css";
import "./styles/Dashboard-v2.css";
import "./styles/MyLeads.css";
import 'react-toastify/dist/ReactToastify.css';
import "./styles/ReportTemplates.css";
import "./styles/MultiValueColumnMode.css";
function App() {
  return (
    <AuthProvider>
      <LeadProvider>
        <Router>
          <ToastContainer position="top-right" autoClose={5000} />
          <AuthExpiredModal />
          <Routes>
            {/* Public / full-screen routes — no shell */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/welcome-pending"
              element={
                <PrivateRoute>
                  <WelcomePending />
                </PrivateRoute>
              }
            />

            {/* Authenticated routes — all inside the shared shell */}
            <Route
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/leads" element={<LeadList />} />
              <Route path="/leads/add" element={<AddLead />} />
              <Route path="/leads/:id" element={<ViewLead />} />
              <Route path="/leads/:id/edit" element={<EditLead />} />
              <Route path="/leads/:id/demo" element={<BookDemo />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/developer" element={ <PrivateRoute adminOnly><DeveloperPortalPage /></PrivateRoute> }/>
              <Route path="/analytics" element={<ManagerAnalyticsPage />} />
              <Route path="/reports" element={<ScheduledExportsPanel />} />
              <Route path="/scheduled-exports" element={<Navigate to="/reports" replace />} />
              <Route path="/reports/templates/new" element={<ReportTemplateBuilder />} />
              <Route path="/reports/templates/:id" element={<ReportTemplateBuilder />} />
              <Route
                path="/admin/*"
                element={
                  <PrivateRoute adminOnly>
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </LeadProvider>
    </AuthProvider>
  );
}

function PrivateRoute({ children, adminOnly = false }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <div className="loading">Loading...</div>;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const approvalStatus =
    user?.approvalStatus || user?.approval_status || "Active";

  if (approvalStatus === "Pending") {
    if (window.location.pathname === "/welcome-pending") {
      return children;
    }
    if (window.location.pathname === "/profile") {
      return children;
    }
    return <Navigate to="/welcome-pending" replace />;
  }

  if (approvalStatus === "Rejected") {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== "Admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default App;